/**
 * Le giocate, per il sito dei clienti.
 *
 * Costruito sullo stesso stampo di creditApi: la piattaforma ha gia' la
 * ricerca con i filtri — e' quella che usa il backoffice — e qui si traduce
 * soltanto fra come parla il servizio (documenti, pagine da zero) e come parla
 * la pagina (righe, pagine da uno). Prima questa pagina chiamava /api/bets,
 * che non esiste in nessun servizio: era rimasta indietro rispetto ai
 * movimenti, che erano gia' stati ricollegati.
 *
 * Il ramo lo decide il servizio, non noi: manda il proprio identificativo
 * nell'intestazione e la ricerca risponde solo con quello che sta sotto chi
 * chiede. Chiedere il ramo di un altro non restituisce niente — non perche'
 * il sito non lo mandi, ma perche' il servizio lo rifiuta.
 */
import axios from 'axios';
import i18next from 'i18next';

const BASE_TICKET = '/v1/ticket-service/admin/ticket';

function gateway() {
  const env = typeof window !== 'undefined' && window.env ? window.env : {};
  return env.REACT_APP_API_URL || '';
}

function intestazioni() {
  const token = typeof window !== 'undefined'
      ? window.localStorage.getItem('access_token') : null;
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const tenant = typeof window !== 'undefined' && window.env && window.env.REACT_APP_API_TENANT;
  if (tenant) headers.TENANT_ID = tenant;
  headers['X-Client-Surface'] = 'FRONTEND';
  return headers;
}

/**
 * Lo stato come lo sceglie una persona, tradotto in quello che il servizio
 * tiene davvero.
 *
 * Sono due campi, non uno: `status` e' dove si trova la giocata (in corso,
 * liquidata, annullata, pagata) e `result` com'e' andata (vinta, persa). Sulla
 * pagina sono una tendina sola, perche' chi guarda non pensa in due colonne.
 */
const DOVE_STA = {
  VOIDED: 'status', PENDING: 'status', PAID: 'status', AWAITING_PAYMENT: 'status',
  DA_ACCETTARE: 'status', RIPROPOSTA: 'status', RIFIUTATA: 'status', SCADUTA: 'status',
  VENDUTA: 'status',
  WON: 'result', LOST: 'result',
};

function condizione(stati) {
  const scelti = Array.isArray(stati) ? stati : (stati ? [stati] : []);
  const status = scelti.filter((s) => DOVE_STA[s] === 'status');
  const result = scelti.filter((s) => DOVE_STA[s] === 'result');
  return {
    status: status.length ? status.join(',') : undefined,
    result: result.length ? result.join(',') : undefined,
  };
}

/**
 * IL GIORNO DIVENTA UN ISTANTE.
 *
 * Il campo data del browser da' "2026-09-01". Il servizio fa
 * Instant.parse() su quella stringa, che fallisce, e nel catch restituisce
 * null: il filtro spariva IN SILENZIO. Nessun errore, nessun avviso — solo
 * tutte le giocate, con la data scritta nel campo a far credere il
 * contrario. E' il tipo di guasto peggiore, quello che non si vede.
 *
 * Il giorno scelto va compreso tutto: il "dal" parte dal suo primo momento,
 * il "al" arriva al suo ultimo. Nell'ora di chi guarda, non in quella del
 * server: chi sceglie "oggi" intende il proprio oggi.
 */
function istante(giorno, fine) {
  if (!giorno) return undefined;
  const d = new Date(`${giorno}T${fine ? '23:59:59.999' : '00:00:00.000'}`);
  return isNaN(d.getTime()) ? undefined : d.toISOString();
}

function corpo(params = {}) {
  return {
    ...condizione(params.stati),
    ticketType: params.type || undefined,
    code: params.code || undefined,
    // Il RAMO, non la persona: scegliendo un'agenzia si vogliono le giocate
    // dei suoi giocatori, perche' un'agenzia non gioca. Filtrare sul suo
    // identificativo non troverebbe niente.
    nodeId: params.nodeId || undefined,
    from: istante(params.dateFrom, false),
    to: istante(params.dateTo, true),
    postaMin: params.postaMin || undefined,
    postaMax: params.postaMax || undefined,
  };
}

/** Il piu' piccolo o il piu' grande fra le colonne, zero se non ce ne sono. */
function estremo(colonne, campo, quale) {
  const valori = (colonne || [])
      .map((c) => Number(c[campo]))
      .filter((v) => !isNaN(v) && v > 0);
  return valori.length ? quale(...valori) : 0;
}

/** L'importo per colonna quando e' lo stesso per tutte, altrimenti null. */
function unicoImporto(colonne) {
  const distinti = [...new Set((colonne || [])
      .map((c) => Number(c.amount))
      .filter((v) => !isNaN(v) && v > 0))];
  return distinti.length === 1 ? distinti[0] : null;
}

/** Quanto paga, al massimo, UNA colonna.
    Non e' `wins.max`: quello e' la somma di tutte le colonne insieme, il
    numero di "se vince tutto". Il massimo di una colonna sola e' il piu'
    alto fra i massimi delle grandezze, che il servizio mette sulle righe
    del sistema quando apre la giocata.
    Quando la colonna e' UNA SOLA il totale e' quella colonna, e allora
    `wins.max` va bene. Quando sono di piu' e gli estremi per grandezza non
    ci sono -- la lista non arricchisce le righe, le arricchisce il
    dettaglio -- non si sa, e torna niente: un trattino e' la risposta
    onesta, un totale spacciato per colonna no. Il discrimine e' quante
    colonne ci sono, non se ci sono le righe del sistema: un'integrale ne ha
    tante e righe di sistema nessuna. */
function massimoColonna(t) {
  const righe = Array.isArray(t.system) ? t.system : [];
  const massimi = righe
      .map((r) => Number(r.vincitaMax))
      .filter((v) => Number.isFinite(v));
  if (massimi.length) return Math.max(...massimi);
  if ((Number(t.multiples) || 1) <= 1) return Number(t.wins && t.wins.max) || 0;
  return null;
}

/** Una giocata come la legge la pagina. */
function riga(t) {
  return {
    id: t.ticketId || t.id,
    ticketCode: t.ticketCode,
    userId: t.userId,
    stake: Number(t.amount) || 0,
    /* Le combinazioni le conta il SERVIZIO (multiples), non la schedina:
       numCombinations arriva dal sito e su un'integrale con due gruppi
       diceva 1. E' lo stesso numero che mostra il backoffice. */
    combinazioni: Number(t.multiples) || 1,
    potentialWin: Number(t.wins && t.wins.max) || 0,
    /* I DUE ESTREMI DELLA VINCITA, presi da dove stanno davvero.
       Il dettaglio li chiedeva e nessuno li metteva: uscivano zero e un
       trattino, su un ticket che nella tabella sotto mostra i suoi numeri.
       `wins` ce li ha tutti e due. */
    potentialWinMin: Number(t.wins && t.wins.min) || 0,
    potentialWinMax: Number(t.wins && t.wins.max) || 0,
    /* IL BONUS, i due estremi di UNA colonna, come nella schedina.
       La schedina lo scrive accanto a "se vince una colonna", e parlano
       della stessa colonna.
       SUI TICKET REGISTRATI PRIMA DEL 3 OTTOBRE 2026 E' ZERO, e zero resta:
       il campo esisteva ma non lo riempiva nessuno -- la schedina il bonus
       lo calcola per mostrarlo e non lo manda, e il servizio non lo
       ricalcolava. Da quella data in poi lo scrive il servizio. */
    bonusMin: Number(t.bonuses && t.bonuses.min) || 0,
    bonusMax: Number(t.bonuses && t.bonuses.max) || 0,
    /* SE VINCE UNA COLONNA, i due estremi.
       Il minimo e' `wins.min`, che e' per definizione la colonna che paga
       meno. Il massimo NON e' `wins.max`: vedi `massimoColonna`. */
    winColMin: Number(t.wins && t.wins.min) || 0,
    winColMax: massimoColonna(t),
    /* LA QUOTA MINIMA E MASSIMA non sono sul ticket: sono le quote delle sue
       colonne, e le colonne ce le abbiamo. Su una singola coincidono. */
    /* DAL SERVIZIO QUANDO C'E'. Il dettaglio manda quotaMin e quotaMax
       gia' fatte, perche' le colonne da cui si ricavavano non viaggiano
       piu' col ticket. Il calcolo vecchio resta per la lista e per le
       giocate antiche. */
    oddsMin: t.quotaMin != null ? Number(t.quotaMin) : estremo(t.bets, 'odd', Math.min),
    oddsMax: t.quotaMax != null ? Number(t.quotaMax) : estremo(t.bets, 'odd', Math.max),
    /* Quanto si gioca su OGNI combinazione. Prima, quando mancava, si
       ripiegava sull'importo totale: su un'integrale da 1 euro in due
       colonne scriveva "1,00 a combinazione" mentre ogni colonna ne aveva
       mezzo. Un sistema con doppie e triple puo' averne di diversi per tipo:
       in quel caso non c'e' UN numero, e si mostra la forbice. */
    amountPerCombination: t.importoColonnaMin != null && t.importoColonnaMax != null
        ? (Number(t.importoColonnaMin) === Number(t.importoColonnaMax)
            ? Number(t.importoColonnaMin) : null)
        : unicoImporto(t.bets),
    amountCombMin: t.importoColonnaMin != null
        ? Number(t.importoColonnaMin) : estremo(t.bets, 'amount', Math.min),
    amountCombMax: t.importoColonnaMax != null
        ? Number(t.importoColonnaMax) : estremo(t.bets, 'amount', Math.max),
    payout: Number(t.paidAmount) || 0,
    status: t.status,
    outcome: t.result,
    type: t.ticketType,
    createdAt: t.placedAt,
    lastEventDate: t.lastEventAt,

    /* IL PERCORSO DELLA RISERVA, e qui c'e' una trappola che vale la pena
       scrivere: questa funzione e' una mappatura CAMPO PER CAMPO, non una
       copia. Tutto quello che non e' elencato qui dentro sparisce, e sparisce
       in silenzio -- dal punto in cui si scrive un campo nuovo sul server non
       si vede che c'e' questo filtro in mezzo, e sembra che il dato non
       arrivi. E' successo: il percorso era stato scritto nel ticket del
       cliente, i dati c'erano sul server, e la riga "Riserva" restava un
       trattino.
       Chi aggiunge un campo sul ticket che il sito deve mostrare lo aggiunga
       anche qui.

       riservataAl e' anche la DISCRIMINANTE: ce l'ha solo chi e' passato
       dalla coda, ed e' quella che decide se la riga "Riserva" si disegna. */
    riservataAl: t.riservataAl,
    scadeIl: t.scadeIl,
    accettataIl: t.accettataIl,
    rifiutataIl: t.rifiutataIl,
    /* Il MOTIVO del rifiuto si', CHI ha deciso no: al giocatore il rifiuto non
       nomina nessuno, e infatti il server accettataDa/rifiutataDa non glieli
       manda nemmeno. */
    motivoRifiuto: t.motivoRifiuto,
    /* LA RIPROPOSTA, intera: e' la domanda che il banco gli sta facendo, e
       senza non puo' rispondere. Il server la manda gia' senza il nome
       dell'operatore (senzaIlPerche); qui si passa com'e'. E' la stessa
       trappola scritta qui sopra: senza questa riga il ticket aperto avrebbe
       lo stato RIPROPOSTA e nessuna proposta da mostrare. */
    riproposta: t.riproposta || null,
    /* IL CASH OUT: quanto ha incassato vendendo, e la posta gia' venduta (una
       vendita parziale lascia la giocata in corso con meno posta). Stessa
       trappola di sopra: senza queste righe non arrivano. */
    cashOutIncassato: Number(t.cashOutIncassato) || 0,
    postaVenduta: Number(t.postaVenduta) || 0,
    cashOut: Array.isArray(t.cashOut) ? t.cashOut : [],
    /* L'ANNULLAMENTO: se l'ha annullata LUI (annullataDalCliente, vero solo
       per l'annullamento dal sito, mai per quello di un operatore), quando,
       e quanto gli e' tornato. Stessa trappola di sopra: senza queste righe
       la lista non saprebbe dire «Annullata da te». */
    annullataDalCliente: !!t.annullataDalCliente,
    voidedAt: t.voidedAt || null,
    refundedAmount: t.refundedAmount != null ? Number(t.refundedAmount) : null,
    /* LA STAMPA E IL TETTO. Quante copie ne ha gia' stampate il servizio
       (null = il contatore non si e' letto: l'anteprima non dice il numero),
       il fuso e il formato delle quote con cui il cliente l'ha giocata, e il
       tetto di vincita in vigore quella volta. Stessa trappola di sopra:
       senza queste righe la giocata stampata non li avrebbe. */
    copieStampate: t.copieStampate != null && Number.isFinite(Number(t.copieStampate))
        ? Number(t.copieStampate) : null,
    fusoVisto: t.fusoVisto || null,
    formatoVisto: t.formatoVisto || null,
    tettoVincita: t.tettoVincita != null ? Number(t.tettoVincita) : null,
    // "5 eventi" vuol dire cinque PARTITE, non cinque selezioni: su un
    // sistema con una doppia le selezioni sono di piu' degli eventi.
    /* IL NUMERO DI EVENTI ARRIVA DAL SERVIZIO, non si conta piu' qui.
       Lo contavamo scorrendo le colonne, e le colonne nella lista non
       arrivano piu': un sistema da 2380 combinazioni pesa megabyte, e
       cinquanta righe cosi' finivano la memoria del servizio. Adesso il
       ticket porta `eventiGiocati`, gli eventi distinti scritti al
       piazzamento. Il conteggio vecchio resta come ripiego per le giocate
       antiche che lo script di riempimento non avesse toccato. */
    eventsCount: Array.isArray(t.eventiGiocati)
        ? t.eventiGiocati.length
        : new Set((t.bets || [])
            .flatMap((g) => (g.matches || []).map((m) => m.eventId))).size,
    /* Prematch, live o tutti e due: due bit scritti al piazzamento, per la
       stessa ragione di sopra. */
    conLive: !!t.conLive,
    conPrematch: !!t.conPrematch,
    // I GRUPPI come li tiene il servizio: una singola e una multipla ne hanno
    // uno solo, un sistema piu' d'uno. Il dettaglio li scorre per mostrare le
    // combinazioni.
    bets: t.bets || [],
    /* LE RIGHE DEL SISTEMA: grandezza, quante combinazioni e quanto costano.
       Erano gia' salvate e pesano niente. Il riquadro "dettaglio sistema"
       legge queste, invece di raggruppare le colonne che non arrivano piu'. */
    system: t.system || [],
    // LE SELEZIONI, una volta sola. Su un sistema la stessa selezione sta in
    // decine di colonne: appiattendole si vedevano dieci righe che erano
    // cinque eventi ripetuti due volte.
    //
    // La chiave tiene dentro anche il SEGNO, non solo la partita: il
    // backoffice raggruppa per partita perche' l'operatore agisce sulla
    // partita, ma chi ha giocato vuole vedere che su quella partita aveva
    // preso sia l'1 sia la X — e' esattamente cio' che ha comprato.
    selezioni: (() => {
        /* Il dettaglio le manda gia' distinte: non ci sono piu' le colonne
           da cui ricavarle, e distinguerle era proprio il lavoro che si
           faceva qui sotto. */
        if (Array.isArray(t.selezioni)) return t.selezioni;
        const viste = new Set();
        const fuori = [];
        (t.bets || []).forEach((g) => (g.matches || []).forEach((m) => {
            const chiave = `${m.eventId}|${m.marketKey}|${m.selectionCode}`;
            if (!viste.has(chiave)) { viste.add(chiave); fuori.push(m); }
        }));
        return fuori;
    })(),
    matches: (t.bets || []).reduce((tutti, g) => tutti.concat(g.matches || []), []),
    raw: t,
  };
}

/**
 * La lista, filtrata e impaginata.
 *
 * Il servizio conta le pagine da zero, la pagina da uno: la differenza sta
 * qui e in nessun altro posto.
 */
export async function getTickets(params = {}) {
  const pagina = Number(params.page) || 1;
  const quante = Number(params.limit) || 20;

  const { data } = await axios.post(
      `${gateway()}${BASE_TICKET}/search`,
      { ...corpo(params), page: pagina - 1, size: quante },
      { headers: intestazioni() });

  const contenuto = Array.isArray(data && data.content) ? data.content : [];

  return {
    tickets: contenuto.map(riga),
    pagination: {
      page: (data && typeof data.number === 'number' ? data.number : pagina - 1) + 1,
      pages: (data && data.totalPages) || 1,
      total: (data && data.totalElements) || contenuto.length,
      limit: quante,
    },
  };
}

/**
 * I numeri in cima alla pagina.
 *
 * Le somme le fa Mongo, non il browser: la domanda e' "come sta andando
 * questo ramo", e scaricare le righe per sommarle a un certo punto smette
 * proprio di funzionare. I tre contatori — in corso, vinte, perse — sono tre
 * conteggi aggregati, non tre elenchi scaricati.
 */
export async function getTicketStats(params = {}) {
  const chiedi = (extra) => axios
      .post(`${gateway()}${BASE_TICKET}/totals`, { ...corpo(params), ...extra },
            { headers: intestazioni() })
      .then((r) => r.data || {})
      .catch(() => ({}));

  const [tutto, inCorso, vinte, perse] = await Promise.all([
    chiedi({}),
    chiedi({ status: 'PENDING', result: undefined }),
    chiedi({ result: 'WON', status: undefined }),
    chiedi({ result: 'LOST', status: undefined }),
  ]);

  const numero = (v) => Number(v) || 0;

  return {
    totalBets: numero(tutto.giocate),
    totalStake: numero(tutto.giocato),
    totalPayout: numero(tutto.vinto),
    potentialWin: numero(tutto.possibile),
    netto: numero(tutto.netto),
    pendingCount: numero(inCorso.giocate),
    wonCount: numero(vinte.giocate),
    lostCount: numero(perse.giocate),
  };
}

/**
 * Una finestra di combinazioni di una giocata.
 *
 * Le combinazioni non viaggiano piu' col ticket: un sistema ne ha migliaia,
 * e portarsele dietro a ogni apertura voleva dire megabyte di rete e di
 * lettura per il browser. Si chiedono qui, cinquanta per volta, e solo
 * quando qualcuno apre quella sezione.
 *
 * Torna { codice, tipo, totale, da, quante, colonne }.
 */
export async function getColonne(codice, da = 0, quante = 50) {
  const { data } = await axios.get(
      `${gateway()}${BASE_TICKET}/${encodeURIComponent(codice)}/colonne`,
      { params: { da, quante }, headers: intestazioni() });
  return data || null;
}

/** Una giocata sola, per il dettaglio. */
export async function getTicket(userId, ticketId) {
  const { data } = await axios.get(
      `${gateway()}/v1/ticket-service/users/${userId}/tickets/${ticketId}`,
      { headers: intestazioni() });
  return data ? riga(data) : null;
}

/**
 * LA RISPOSTA ALLA RIPROPOSTA: 'accetta' o 'rifiuta'.
 *
 * Non manda numeri: la proposta e' gia' scritta sul ticket e si accetta o si
 * rifiuta com'e'. Torna quello che risponde il servizio -- { stato, ... } --
 * e lo stato e' quello in cui la giocata E' adesso: PENDING se e' partita,
 * RIFIUTATA se ha detto no, ma anche SCADUTA o RIFIUTATA se nel frattempo il
 * tempo era finito o una partita era cominciata. Chi chiama guarda lo stato,
 * non da' per scontato che un si' sia diventato PENDING.
 * Un errore arriva come eccezione axios col corpo { code, message }.
 */
export async function rispondiRiproposta(userId, ticketId, risposta) {
  const { data } = await axios.post(
      `${gateway()}/v1/ticket-service/users/${userId}/tickets/${ticketId}/riproposta/${risposta}`,
      {},
      { headers: intestazioni() });
  return data || null;
}

/**
 * "L'ho vista": si chiama nel momento in cui la proposta viene DISEGNATA, e
 * scrive arrivataIl sulla proposta. Serve all'operatore, che altrimenti
 * aspetta una risposta da uno che non ha mai visto niente. Non torna niente
 * e non puo' far fallire la pagina: se non passa, la proposta si vede lo
 * stesso e il tempo corre lo stesso.
 */
export function segnaRipropostaArrivata(userId, ticketId) {
  return axios.post(
      `${gateway()}/v1/ticket-service/users/${userId}/tickets/${ticketId}/riproposta/arrivata`,
      {},
      { headers: intestazioni() }).catch(() => null);
}

/**
 * IL CASH OUT: le offerte per le giocate della pagina, una giocata sola, la
 * vendita.
 *
 * Solo per le proprie giocate: il servizio rifiuta (403) chi chiede per un
 * altro, anche un'agenzia per un suo giocatore. Le offerte tornano come le
 * scrive il servizio: { ticketId, disponibile, offerta, posta,
 * vincitaPotenziale, parziale, percentualeMinima, tolleranza } oppure
 * { ticketId, disponibile: false, motivo, messaggio }. La trattenuta del banco
 * non c'e', e non deve esserci.
 */
export async function getOfferteCashOut(userId, ticketIds) {
  if (!userId || !ticketIds || !ticketIds.length) return [];
  const { data } = await axios.post(
      `${gateway()}/v1/ticket-service/users/${userId}/cashout/offerte`,
      { ticketIds },
      { headers: intestazioni() });
  return Array.isArray(data) ? data : [];
}

export async function getOffertaCashOut(userId, ticketId) {
  const { data } = await axios.get(
      `${gateway()}/v1/ticket-service/users/${userId}/tickets/${ticketId}/cashout`,
      { headers: intestazioni() });
  return data || null;
}

/**
 * VENDE: tutta (100) o una parte, al prezzo che il giocatore ha visto.
 *
 * Torna { http, ...corpo }: 200 venduta, 202 accredito in verifica. Un
 * rifiuto arriva come eccezione axios col corpo { code, message, params }:
 * CASHOUT_OFFERTA_CAMBIATA porta l'offerta nuova in params.offerta. La
 * chiave e' della conferma: ripetuta (rete caduta a meta') il servizio
 * risponde com'e' andata la prima volta, e non vende due volte.
 */
export async function vendiCashOut(userId, ticketId, { percentuale, offertaVista, chiave }) {
  const r = await axios.post(
      `${gateway()}/v1/ticket-service/users/${userId}/tickets/${ticketId}/cashout`,
      { percentuale, offertaVista, chiave },
      { headers: intestazioni() });
  return { http: r.status, ...(r.data || {}) };
}

/**
 * L'ANNULLAMENTO DAL CLIENTE: la sua giocata appena fatta, entro il tempo e
 * sotto l'importo che decide il backoffice (Impostazioni schedina ›
 * Annullamento dal cliente).
 *
 * La domanda torna { annullabile, motivo, messaggio, restaSecondi,
 * entroSecondi }. restaSecondi lo conta il SERVIZIO (fino alla fine del
 * tempo o all'inizio della prima partita, quello che viene prima): il sito
 * lo usa come durata da adesso, e non confronta mai l'ora del servizio con
 * quella del browser. E' solo un suggerimento: decide l'annullamento.
 * Solo per le proprie giocate: chi chiede per un altro prende 403.
 */
export async function getAnnullamento(userId, ticketId) {
  const r = await axios.get(
      `${gateway()}/v1/ticket-service/users/${userId}/tickets/${ticketId}/annullamento`,
      { headers: intestazioni() });
  return { http: r.status, ...(r.data || {}) };
}

/**
 * ANNULLA: senza corpo, si annulla tutta e torna tutta la posta.
 *
 * Torna { http, ...corpo }: 200 { stato: 'VOIDED', ticketId, ticketCode,
 * rimborsato }, 202 { stato: 'IN_ANNULLO', motivo: 'ANNULLO_IN_VERIFICA',
 * messaggio } quando il rimborso e' in verifica. Un rifiuto arriva come
 * eccezione axios col corpo { code, status, message, params }
 * (ANNULLO_TEMPO_SCADUTO, ANNULLO_IMPORTO_TROPPO_ALTO, ...). Non serve una
 * chiave: la chiave e' la giocata stessa, che si annulla una volta sola, e
 * ripetuta dopo una rete caduta il servizio risponde com'e' andata.
 */
export async function annullaGiocata(userId, ticketId) {
  const r = await axios.post(
      `${gateway()}/v1/ticket-service/users/${userId}/tickets/${ticketId}/annullamento`,
      {},
      { headers: intestazioni() });
  return { http: r.status, ...(r.data || {}) };
}

/**
 * LA STAMPA DI UNA GIOCATA: chiede al servizio il numero della copia.
 *
 * Ogni stampa si conta e finisce nel registro, anche quelle rifiutate. Il
 * formato NON lo manda il sito: lo decide il servizio dalle regole di chi
 * stampa (Impostazioni schedina › Stampa e condivisione) e lo rimanda
 * indietro, insieme a conLogo, che (sull'A4 come sul rotolo) vale solo se
 * la regola lo concede. Si stampa con quello che torna, non con la copia delle regole che
 * ha la pagina: una pagina aperta prima di un cambio nel backoffice
 * stamperebbe il formato vecchio.
 *
 * Torna { copia, ticketCode, stampataIl, formato, conLogo }. Un rifiuto
 * arriva come eccezione axios col corpo { code, message, params }:
 * STAMPA_SPENTA, STAMPA_NON_STAMPABILE (params.stato), STAMPA_NON_TUA,
 * PROFILE_UNAVAILABLE. Senza numero non si stampa.
 */
export async function registraStampa(userId, ticketId, { conLogo } = {}) {
  const { data } = await axios.post(
      `${gateway()}/v1/ticket-service/users/${userId}/tickets/${ticketId}/stampe`,
      { conLogo: !!conLogo },
      { headers: intestazioni() });
  return data || null;
}

/* Un rifiuto del servizio, nella forma che la pagina scrive: codice, stato e
   frase. Senza risposta e' la rete. */
function rifiuto(e) {
  const r = e && e.response;
  if (!r) {
    return { code: 'RETE', status: 0, params: {},
      message: i18next.t('Non si riesce a raggiungere il server: riprova fra poco') };
  }
  const corpo = r.data || {};
  return {
    code: corpo.code || `HTTP_${r.status}`,
    status: r.status,
    message: corpo.message || i18next.t('Il collegamento non si è potuto preparare'),
    params: corpo.params || {},
  };
}

/**
 * «GIOCA ANCHE TU»: la prenotazione con gli eventi di una giocata, per
 * condividerla.
 *
 * La costruisce il servizio, senza la posta: chi riceve il collegamento
 * trova gli eventi e mette il suo importo. Una giocata ancora valida torna
 * la stessa prenotazione (riusata: true) invece di farne un'altra. La skin
 * la dice l'intestazione TENANT_ID, come per le altre prenotazioni.
 *
 * NON LANCIA MAI: torna { ok: true, prenotazione, riusata } oppure
 * { ok: false, errore: { code, status, message, params } }. Un rifiuto vuol
 * dire «si condivide senza collegamento», non un errore della pagina.
 */
export async function prenotazioneDaGiocata(userId, ticketId) {
  try {
    const r = await axios.post(
        `${gateway()}/v1/ticket-service/users/${userId}/tickets/${ticketId}/prenotazione`,
        undefined,
        { headers: intestazioni() });
    const prenotazione = r.data || null;
    if (!prenotazione || !prenotazione.codice) {
      return { ok: false, errore: rifiuto({ response: { status: r.status, data: {} } }) };
    }
    return { ok: true, prenotazione, riusata: !!prenotazione.riusata };
  } catch (e) {
    return { ok: false, errore: rifiuto(e) };
  }
}

/**
 * LA RETE, per due domande diverse.
 *
 * Il ticket porta solo l'identificativo di chi ha giocato: una stringa di
 * trentasei caratteri che non dice niente a nessuno. Il NOME UTENTE sta
 * altrove, nell'elenco degli utenti, e si legge una volta sola per tutta la
 * pagina — una chiamata per riga su cento giocate sarebbero cento chiamate.
 *
 * La FORMA della rete — chi sta sotto chi — sta invece nell'albero, ed e'
 * un'altra cosa ancora. Serve alle tendine, e serve presa da li' e non
 * scritta a mano: i livelli arrivano dai dati, quindi un livello nuovo
 * compare da solo senza toccare questa pagina.
 *
 * Tutti e due rispondono gia' ristretti al ramo di chi chiede: il servizio
 * legge USER_ID che il gateway mette nella richiesta e taglia da solo.
 * Chiedere di piu' non serve, e non funzionerebbe.
 */
export async function getUtentiRete() {
  const { data } = await axios.get(`${gateway()}/admin/users`,
      { headers: intestazioni() });
  if (Array.isArray(data)) return data;
  return (data && data.users) || [];
}

/**
 * L'albero della rete NON sta piu' qui.
 *
 * Stava in questo file perche' la pagina delle giocate e' stata la prima ad
 * averne bisogno, ma la rete non e' una faccenda delle giocate: e' finito che
 * tre file chiamavano /private/jurisdiction/all ciascuno a modo suo. Ora la
 * chiamata e' una sola, in services/reteApi.js, e questa la rimanda li' perche'
 * chi importava da qui continui a funzionare.
 */
export { nodiDellaMiaRete as getAlberoRete } from './reteApi';

/* Per il flusso del giocatore (services/flussoGiocate.js), che chiede il
   biglietto con le stesse intestazioni e apre l'EventSource sullo stesso
   gateway: due copie di queste due funzioni sarebbero due posti dove un
   giorno il gettone si manda in un modo diverso. */
export { gateway, intestazioni };

const ticketsApi = {
  getTickets, getTicketStats, getTicket, getUtentiRete,
  rispondiRiproposta, segnaRipropostaArrivata,
  getOfferteCashOut, getOffertaCashOut, vendiCashOut,
  getAnnullamento, annullaGiocata,
  registraStampa, prenotazioneDaGiocata,
};
export default ticketsApi;
